/**
 * FindingDetailPage — `/findings/:findingId`.
 *
 * The cornerstone view for dual-path validation. When a finding's
 * verdict_source is `both_agree` or `both_disagree`, renders the
 * deterministic-path verdict and the LLM-path verdict side-by-side so
 * the auditor can immediately see where they differ. For single-path
 * findings (deterministic-only or LLM-only rules), only the relevant
 * side renders.
 *
 * Resolution actions — Accept / Waive / False positive — are gated on
 * the validation.resolve permission. A notes textarea lets the auditor
 * record why they chose that disposition.
 *
 * When the finding has a paired_finding (the other half of a
 * both_disagree pair), a link to that finding is surfaced so the
 * auditor can resolve both halves.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, XCircle, AlertTriangle, GitCompare, Link2,
} from 'lucide-react';
import { AppShell } from '@/acc-ui/components/AppShell';
import { PageHeader, Card, SectionLabel } from '@/acc-ui/primitives/Layout';
import { Badge } from '@/acc-ui/primitives/Badge';
import { Button } from '@/acc-ui/primitives/Button';
import { Textarea } from '@/acc-ui/primitives/Textarea';
import { validationApi } from '@/api/validation-api';
import { usePermission } from '@/auth/use-permission';
import {
  FINDING_STATUS,
  VALIDATION_RESOLVE,
  VERDICT_SOURCE,
  type FindingDTO,
  type RuleVerdictDTO,
  type Severity,
} from '@loanaudit/shared';
import { VerdictSourceBadge } from './FindingsPanel';

// -------------------------------------------------------------------------

const SEVERITY_TONE: Record<Severity, 'danger' | 'warning' | 'info' | 'muted' | 'neutral'> = {
  critical: 'danger',
  high: 'danger',
  medium: 'warning',
  low: 'info',
  info: 'muted',
};

// Human-readable labels — underlying enum values stay for logic.
const SEVERITY_LABEL: Record<string, string> = {
  critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low', info: 'Info',
};
const STATUS_LABEL: Record<string, string> = {
  open: 'Open', accepted: 'Accepted', waived: 'Waived',
  false_positive: 'False positive', superseded: 'Superseded',
};
const VERDICT_LABEL: Record<string, string> = {
  violated: 'Violated', compliant: 'Compliant', inconclusive: 'Inconclusive',
};

// -------------------------------------------------------------------------

export function FindingDetailPage() {
  const { findingId } = useParams<{ findingId: string }>();
  const qc = useQueryClient();
  const { has } = usePermission();
  const canResolve = has(VALIDATION_RESOLVE);

  const [notes, setNotes] = useState('');
  const [actionPending, setActionPending] = useState<'accept' | 'waive' | 'false_positive' | null>(
    null,
  );

  const query = useQuery({
    queryKey: ['validation', 'finding', findingId],
    queryFn: () => validationApi.getFinding(findingId!),
    enabled: !!findingId,
  });

  const resolve = useMutation({
    mutationFn: (action: 'accept' | 'waive' | 'false_positive') =>
      validationApi.resolveFinding(findingId!, { action, notes: notes.trim() || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['validation', 'finding', findingId] });
      qc.invalidateQueries({ queryKey: ['validation', 'findings'] });
      qc.invalidateQueries({ queryKey: ['validation', 'loanFile'] });
    },
    onSettled: () => setActionPending(null),
  });

  if (query.isLoading) {
    return (
      <AppShell title="Finding">
        <Card>
          <div className="py-8 text-center text-sm text-text-muted">Loading finding…</div>
        </Card>
      </AppShell>
    );
  }
  if (!query.data) return null;

  const { finding, pairedFinding } = query.data;
  const severity = finding.severity as Severity;
  const isDisagree = finding.verdictSource === VERDICT_SOURCE.BOTH_DISAGREE;
  const isAgree = finding.verdictSource === VERDICT_SOURCE.BOTH_AGREE;

  return (
    <AppShell title={`Finding · ${finding.ruleId}`}>
      <div className="space-y-4">
        <Link
          to="/findings"
          className="inline-flex items-center gap-1 text-sm text-brand-blue hover:underline"
        >
          <ArrowLeft className="w-4 h-4" /> All findings
        </Link>
        <PageHeader
          title={finding.title}
          subtitle={`${finding.ucId.toUpperCase()} · ${finding.ruleId}`}
          actions={
            <div className="flex items-center gap-2">
              <Badge tone={SEVERITY_TONE[severity]}>{SEVERITY_LABEL[severity] ?? severity}</Badge>
              <VerdictSourceBadge source={finding.verdictSource} />
              <Badge tone={finding.status === FINDING_STATUS.OPEN ? 'warning' : 'muted'}>
                {STATUS_LABEL[finding.status] ?? finding.status}
              </Badge>
            </div>
          }
        />

        {finding.loanNumber && (
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <SectionLabel>Loan file</SectionLabel>
                <div className="mt-1 font-mono text-sm">{finding.loanNumber}</div>
              </div>
              <Link
                to={`/loan-files/${finding.loanFileId}`}
                className="text-sm text-brand-blue hover:underline flex items-center gap-1"
              >
                Open loan file <Link2 className="w-3.5 h-3.5" />
              </Link>
            </div>
          </Card>
        )}

        {isDisagree && (
          <Card className="border-amber-300 bg-amber-50/40">
            <div className="flex items-start gap-2">
              <GitCompare className="w-4 h-4 text-amber-700 mt-0.5" />
              <div>
                <div className="font-semibold text-amber-900">The two paths disagreed</div>
                <p className="text-sm text-amber-800 mt-1">
                  The deterministic rule and the LLM reached different conclusions on this
                  rule. Each side below shows what each path saw and concluded.{' '}
                  {pairedFinding ? (
                    <>
                      The paired finding is{' '}
                      <Link
                        to={`/findings/${pairedFinding.id}`}
                        className="text-brand-blue underline"
                      >
                        here
                      </Link>
                      .
                    </>
                  ) : null}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Side-by-side dual path */}
        <div className="grid md:grid-cols-2 gap-4">
          <VerdictColumn
            title="Deterministic rule"
            tone={isAgree ? 'success' : isDisagree ? 'warning' : 'neutral'}
            verdict={finding.deterministicVerdict}
            fallback="Deterministic path did not run for this finding."
          />
          <VerdictColumn
            title="LLM verdict"
            tone={isAgree ? 'success' : isDisagree ? 'warning' : 'info'}
            verdict={finding.llmVerdict}
            fallback="Deterministic rule only — LLM cross-check not applicable for this check."
          />
        </div>

        <Card>
          <SectionLabel>Evidence</SectionLabel>
          <EvidenceView evidence={finding.evidence} />
        </Card>

        {finding.status === FINDING_STATUS.OPEN ? (
          canResolve ? (
            <Card>
              <SectionLabel>Resolve</SectionLabel>
              <Textarea
                className="mt-2"
                rows={3}
                placeholder="Notes (optional — required for waive)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
              <div className="mt-3 flex items-center gap-2">
                <Button
                  variant="primary"
                  leftIcon={<CheckCircle2 className="w-4 h-4" />}
                  loading={actionPending === 'accept'}
                  disabled={resolve.isPending}
                  onClick={() => {
                    setActionPending('accept');
                    resolve.mutate('accept');
                  }}
                >
                  Accept
                </Button>
                <Button
                  variant="secondary"
                  leftIcon={<AlertTriangle className="w-4 h-4" />}
                  loading={actionPending === 'waive'}
                  disabled={resolve.isPending || notes.trim().length === 0}
                  onClick={() => {
                    setActionPending('waive');
                    resolve.mutate('waive');
                  }}
                >
                  Waive (accepted deviation)
                </Button>
                <Button
                  variant="ghost"
                  leftIcon={<XCircle className="w-4 h-4" />}
                  loading={actionPending === 'false_positive'}
                  disabled={resolve.isPending}
                  onClick={() => {
                    setActionPending('false_positive');
                    resolve.mutate('false_positive');
                  }}
                >
                  False positive
                </Button>
              </div>
            </Card>
          ) : (
            <Card>
              <p className="text-sm text-text-muted">
                You don't have permission to resolve findings. Ask a Senior Auditor or
                Audit Manager for help.
              </p>
            </Card>
          )
        ) : (
          <Card>
            <SectionLabel>Resolution</SectionLabel>
            <div className="mt-2 text-sm">
              <Badge tone="success">{STATUS_LABEL[finding.status] ?? finding.status}</Badge>{' '}
              {finding.resolvedByEmail && (
                <span className="text-text-muted">
                  by {finding.resolvedByEmail}
                  {finding.resolvedAt
                    ? ` on ${new Date(finding.resolvedAt).toLocaleString()}`
                    : ''}
                </span>
              )}
            </div>
            {finding.resolutionNotes && (
              <div className="mt-2 whitespace-pre-wrap text-sm text-text-secondary">
                {finding.resolutionNotes}
              </div>
            )}
          </Card>
        )}
      </div>
    </AppShell>
  );
}

// -------------------------------------------------------------------------
// Verdict column — renders one path's verdict with a tone-appropriate header
// -------------------------------------------------------------------------

function VerdictColumn({
  title,
  tone,
  verdict,
  fallback,
}: {
  title: string;
  tone: 'success' | 'warning' | 'info' | 'neutral';
  verdict: RuleVerdictDTO | null;
  fallback: string;
}) {
  if (!verdict) {
    return (
      <Card>
        <SectionLabel>{title}</SectionLabel>
        <p className="text-sm text-text-muted mt-2">{fallback}</p>
      </Card>
    );
  }
  const verdictTone =
    verdict.verdict === 'violated'
      ? 'danger'
      : verdict.verdict === 'compliant'
        ? 'success'
        : verdict.verdict === 'inconclusive'
          ? 'muted'
          : 'warning';
  void tone;
  return (
    <Card>
      <div className="flex items-center justify-between">
        <SectionLabel>{title}</SectionLabel>
        <Badge tone={verdictTone as 'danger' | 'success' | 'muted' | 'warning'}>
          {VERDICT_LABEL[verdict.verdict] ?? verdict.verdict}
        </Badge>
      </div>
      {verdict.title && (
        <div className="mt-2 font-medium text-text-primary">{verdict.title}</div>
      )}
      {verdict.description && (
        <p className="mt-1 text-sm text-text-secondary whitespace-pre-wrap">
          {verdict.description}
        </p>
      )}
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-text-muted">
        <div>
          <span className="font-semibold">Confidence:</span>{' '}
          {(verdict.confidence * 100).toFixed(0)}%
        </div>
        {verdict.severity && (
          <div>
            <span className="font-semibold">Severity:</span> {verdict.severity}
          </div>
        )}
        {verdict.modelId && (
          <div className="col-span-2 truncate">
            <span className="font-semibold">Model:</span> {verdict.modelId}
          </div>
        )}
        {(verdict.tokenInput != null || verdict.tokenOutput != null) && (
          <div className="col-span-2">
            <span className="font-semibold">Tokens:</span> {verdict.tokenInput ?? 0} in ·{' '}
            {verdict.tokenOutput ?? 0} out · {verdict.latencyMs ?? 0}ms
          </div>
        )}
      </div>
      {verdict.evidence?.description && (
        <div className="mt-3 text-xs p-2 rounded-md bg-slate-50 text-text-secondary whitespace-pre-wrap">
          {verdict.evidence.description}
        </div>
      )}
    </Card>
  );
}

// -------------------------------------------------------------------------
// Evidence renderer — compact JSON-like display of fields + computation
// -------------------------------------------------------------------------

function EvidenceView({ evidence }: { evidence: FindingDTO['evidence'] }) {
  if (!evidence) return <p className="text-sm text-text-muted mt-2">No evidence recorded.</p>;
  return (
    <div className="mt-2 space-y-3 text-sm">
      {evidence.description && (
        <p className="text-text-secondary">{evidence.description}</p>
      )}
      {evidence.documents && evidence.documents.length > 0 && (
        <div>
          <SectionLabel>Documents</SectionLabel>
          <ul className="mt-1 space-y-0.5">
            {evidence.documents.map((d) => (
              <li key={d.documentId} className="text-xs">
                <span className="font-mono text-text-muted">{d.documentType}</span>{' '}
                {d.fileName}
              </li>
            ))}
          </ul>
        </div>
      )}
      {evidence.comparison && (
        <div>
          <SectionLabel>Comparison</SectionLabel>
          <div className="mt-1 grid grid-cols-2 gap-2 text-xs">
            <Compare
              label={evidence.comparison.leftLabel}
              value={String(evidence.comparison.leftValue)}
            />
            <Compare
              label={evidence.comparison.rightLabel}
              value={String(evidence.comparison.rightValue)}
            />
          </div>
          {evidence.comparison.toleranceApplied !== undefined && (
            <p className="mt-1 text-xs text-text-muted">
              Tolerance ±{evidence.comparison.toleranceApplied}{' '}
              {evidence.comparison.toleranceUnit ?? ''}
            </p>
          )}
        </div>
      )}
      {evidence.computation && (
        <div>
          <SectionLabel>Computation</SectionLabel>
          <div className="mt-1 text-xs font-mono bg-slate-50 p-2 rounded-md whitespace-pre-wrap break-all">
            {evidence.computation.formulaName}
            {'\n'}inputs: {JSON.stringify(evidence.computation.inputs)}
            {'\n'}recomputed: {evidence.computation.recomputedValue} · stated:{' '}
            {evidence.computation.statedValue}
            {evidence.computation.deltaPct !== undefined &&
              ` · Δ ${evidence.computation.deltaPct} pp`}
          </div>
        </div>
      )}
      {evidence.fields && evidence.fields.length > 0 && (
        <div>
          <SectionLabel>Fields</SectionLabel>
          <ul className="mt-1 space-y-0.5">
            {evidence.fields.map((f, i) => (
              <li key={i} className="text-xs font-mono">
                {f.documentType ? `${f.documentType}.` : ''}
                {f.fieldPath} = {JSON.stringify(f.value)}
                {f.extractionConfidence !== undefined &&
                  ` (conf ${(f.extractionConfidence * 100).toFixed(0)}%)`}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Compare({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-2 rounded-md bg-slate-50">
      <div className="text-micro uppercase tracking-wide text-text-muted">
        {label}
      </div>
      <div className="mt-0.5 font-mono">{value}</div>
    </div>
  );
}

// End of file
